import type { StaffPaymentsApi, StaffQuotesApi } from "@lafayette/api-client";
import { createContext, type ReactNode, useContext } from "react";

// What the Payments screens and the quote panel of a request need from outside: the typed API clients and the clock. The admin app
// supplies the real ones (`admin-runtime.ts`); a test or the development harness supplies fakes. The provider sits ABOVE the routes, so the
// quote panel of the request page reads the same runtime as the Payments pages.

export type PaymentsRuntime = Readonly<{
  payments: StaffPaymentsApi;
  quotes: StaffQuotesApi;
  now: () => Date;
}>;

const RuntimeContext = createContext<PaymentsRuntime | null>(null);

export function PaymentsRuntimeProvider({
  value,
  children,
}: Readonly<{ value: PaymentsRuntime; children: ReactNode }>) {
  return <RuntimeContext.Provider value={value}>{children}</RuntimeContext.Provider>;
}

export function usePaymentsRuntime(): PaymentsRuntime {
  const runtime = useContext(RuntimeContext);
  if (runtime === null) throw new Error("The payment pages need a PaymentsRuntimeProvider.");
  return runtime;
}

/** The runtime when there is one, else `null`: a page that offers payment help only where the host wires it (the request page). */
export function useOptionalPaymentsRuntime(): PaymentsRuntime | null {
  return useContext(RuntimeContext);
}
